import React, { useState, forwardRef, useImperativeHandle, useRef } from "react";
import Layout from "../layout";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate, Link } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import { GoogleLogin } from "@react-oauth/google";
import { ROOT_API } from "../../api/config";
import { updateUserInformation } from "../../stores/users/userSlice";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import "./style.scss";
import { ErrorCode } from "../../api/ErrorCode";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useSelector, useDispatch } from "react-redux";

const schema = yup.object().shape({
  email: yup.string().required(<FormattedMessage id="email_not_blank" />),
  password: yup.string().required(<FormattedMessage id="password_not_blank" />),
});

const forgotPasswordSchema = yup.object().shape({
  email: yup.string().required(<FormattedMessage id="email_not_blank" />),
  otp: yup
    .string()
    .required(<FormattedMessage id="verification_code_not_blank" />),
  password: yup.string().required(<FormattedMessage id="password_not_blank" />),
  confirm_password: yup
    .string()
    .oneOf(
      [yup.ref("password")],
      <FormattedMessage id="confirm_password_not_correct" />
    ),
});

const ForgotPassword = forwardRef((props, ref) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(forgotPasswordSchema),
  });

  const intl = useIntl();

  const [email, setEmail] = useState("");

  const [open, setOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const selectedLocale =
    useSelector((state) => state.lang.value) ||
    localStorage.getItem("lang") ||
    "hr";

  const [isGettingOtp, setIsGettingOtp] = useState(false);

  const generateOtp = async () => {
    const axiosObject = axios.create({
      headers: {
        "Content-Type": "application/json",
        lang: selectedLocale,
      },
    });
    setIsGettingOtp(true);
    try {
      const res = await axiosObject.post(`${ROOT_API}apis/generate-otp`, {
        email: email,
      });
    } catch (error) {
      handleClose();
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          error &&
          error.response &&
          error.response.data &&
          error.response.data.error_code
            ? intl.formatMessage({
                id: ErrorCode[error.response.data.error_code],
              })
            : intl.formatMessage({
                id: "there_are_error_occured",
              }),
      });
    }
    setIsGettingOtp(false);
  };

  useImperativeHandle(ref, () => ({
    handleClose,
    handleOpen,
  }));

  const navigation = useNavigate();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await axios.post(`${ROOT_API}apis/forgot-password`, data);
      handleClose();
      Swal.fire({
        icon: "success",
        title: intl.formatMessage({
          id: "change_password_success",
        }),
        confirmButtonText: "OK",
      }).then((result) => {
        if (result.isConfirmed) {
          navigation("/login");
        }
      });
    } catch (error) {
      handleClose();
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          error &&
          error.response &&
          error.response.data &&
          error.response.data.error_code
            ? intl.formatMessage({
                id: ErrorCode[error.response.data.error_code],
              })
            : intl.formatMessage({
                id: "there_are_error_occured",
              }),
      });
    }
    setIsLoading(false);
  };

  return (
    <div>
      <Dialog open={open} onClose={handleClose} sx={{ margin: "0px" }}>
        <DialogTitle>
          {intl.formatMessage({ id: "forgot_password" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage({ id: "forgot_password_detail" })}
          </DialogContentText>
          <form className="pt-36" onSubmit={handleSubmit(onSubmit)}>
            <div className="general-form-main-form_group mb-12">
              <div className="general-form-main-form_group_code flex gap-10">
                <input
                  className="px-10 py-12 radius-4 font-16"
                  placeholder={intl.formatMessage({ id: "email" })}
                  {...register("email")}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                />
                {errors.email && (
                  <p className="error font-14 mt-2 general-form-main-form_group_code_title-phone">
                    {errors.email.message}
                  </p>
                )}
                {isGettingOtp ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <button
                    className="pointer font-14 text-center"
                    style={{
                      color: "var(--tungsten-deep)",
                      background: "none",
                      border: 0,
                    }}
                    type="button"
                    onClick={generateOtp}
                  >
                    {intl.formatMessage({ id: "get_code" })}
                  </button>
                )}
              </div>
              {errors.email && (
                <p className="error font-14 mt-2 general-form-main-form_group_code_title-laptop">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div className="general-form-main-form_group mb-12">
              <input
                className="px-10 py-12 radius-4 font-16"
                placeholder={intl.formatMessage({ id: "code" })}
                {...register("otp")}
              />
              {errors.otp && (
                <p className="error font-14 mt-2">{errors.otp.message}</p>
              )}
            </div>
            <div className="general-form-main-form_group mb-12">
              <input
                className="px-10 py-12 radius-4 font-16"
                placeholder={intl.formatMessage({ id: "new_password" })}
                type="password"
                {...register("password")}
              />
              {errors.password && (
                <p className="error font-14 mt-2">{errors.password.message}</p>
              )}
            </div>
            <div className="general-form-main-form_group mb-12">
              <input
                className="px-10 py-12 radius-4 font-16"
                placeholder={intl.formatMessage({
                  id: "confirm_new_password",
                })}
                type="password"
                {...register("confirm_password")}
              />
              {errors.confirm_password && (
                <p className="error font-14 mt-2">
                  {errors.confirm_password.message}
                </p>
              )}
            </div>
          </form>
        </DialogContent>
        <Box
          sx={{
            display: isLoading ? "flex" : "none",
            justifyContent: "center",
            marginBottom: "6px",
          }}
        >
          <CircularProgress />
        </Box>
        <DialogActions style={{ marginBottom: "12px", padding: "0px 24px" }}>
          <button
            className="btn-cta"
            style={{ width: "100%" }}
            type="submit"
            onClick={handleSubmit(onSubmit)}
          >
            {intl.formatMessage({ id: "submit" })}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
});

const Login = () => {
  const forgotPasswordRef = useRef();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(schema),
  });

  const intl = useIntl();

  const dispatch = useDispatch();

  const selectedLocale =
    useSelector((state) => state.lang.value) ||
    localStorage.getItem("lang") ||
    "hr";

  const [isLoading, setIsLoading] = useState(false);

  const handleClickOpen = () => {
    forgotPasswordRef.current.handleOpen();
  };

  const navigation = useNavigate();

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const res = await axios.post(`${ROOT_API}apis/login`, data);
      if (res && res.data) {
        localStorage.setItem("access_token", res.data.data.access_token);
        dispatch(updateUserInformation(res.data.data.user_data));
        navigation("/pocetna");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          error &&
          error.response &&
          error.response.data &&
          error.response.data.error_code
            ? intl.formatMessage({
                id: ErrorCode[error.response.data.error_code],
              })
            : intl.formatMessage({
                id: "there_are_error_occured",
              }),
      });
    }
    setIsLoading(false);
  };

  const showGoogleError = () => {
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: intl.formatMessage({ id: "there_are_error_occured" }),
    });
  };

  const onGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse || !credentialResponse.credential) {
      showGoogleError();
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.post(`${ROOT_API}apis/auth/google`, {
        credential: credentialResponse.credential,
      });
      if (res && res.data) {
        localStorage.setItem("access_token", res.data.data.access_token);
        dispatch(updateUserInformation(res.data.data.user_data));
        navigation("/pocetna");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          error &&
          error.response &&
          error.response.data &&
          error.response.data.error_code
            ? intl.formatMessage({
                id: ErrorCode[error.response.data.error_code],
              })
            : intl.formatMessage({
                id: "there_are_error_occured",
              }),
      });
    }
    setIsLoading(false);
  };

  return (
    <Layout>
      <div className="general-form">
        <div className="container">
          <div className="general-form-main">
            <form
              className="general-form-main-form p-36"
              onSubmit={handleSubmit(onSubmit)}
            >
              <p className="font-bold font-20 text-center mb-22">
                <FormattedMessage id="login" />
              </p>
              <div className="general-form-main-form_group mb-12">
                <input
                  className="px-10 py-12 radius-4 font-16"
                  placeholder={intl.formatMessage({ id: "email" })}
                  type="email"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="error font-14 mt-2">{errors.email.message}</p>
                )}
              </div>
              <div className="general-form-main-form_group mb-12">
                <input
                  className="px-10 py-12 radius-4 font-16"
                  placeholder={intl.formatMessage({ id: "password" })}
                  {...register("password")}
                  type="password"
                />
                {errors.password && (
                  <p className="error font-14 mt-2">
                    {errors.password.message}
                  </p>
                )}
              </div>
              <div
                className="general-form-main-form_forgot-password mb-12"
                onClick={handleClickOpen}
              >
                <FormattedMessage id="forgot_password" />
              </div>
              <Box
                sx={{
                  display: isLoading ? "flex" : "none",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
              <button className="btn-cta general-form-main-form_submit mt-40">
                <FormattedMessage id="submit" />
              </button>
              <div className="general-form-main-form_register mt-20 text-center pointer">
                <Link
                  to="/register"
                  style={{ textDecoration: "none", color: "var(--tungsten-deep)" }}
                >
                  <FormattedMessage id="register_an_account" />
                </Link>
              </div>
              {googleClientId && (
                <>
                  <Divider sx={{ marginTop: "20px", marginBottom: "16px" }}>
                    <span className="font-14" style={{ color: "#888" }}>
                      <FormattedMessage id="or" />
                    </span>
                  </Divider>
                  <Box
                    sx={{ display: "flex", justifyContent: "center" }}
                    aria-label={intl.formatMessage({ id: "login_with_google" })}
                  >
                    <GoogleLogin
                      onSuccess={onGoogleSuccess}
                      onError={showGoogleError}
                      text="signin_with"
                      locale={selectedLocale}
                      width="280"
                    />
                  </Box>
                </>
              )}
            </form>
          </div>
          <ForgotPassword ref={forgotPasswordRef} />
        </div>
      </div>
    </Layout>
  );
};

export default Login;
